import React, { useMemo } from 'react'
import { Polyline, Tooltip } from 'react-leaflet'

// Expanded color palette for better route distinction
const COLORS = [
  '#3b82f6', // Blue
  '#ef4444', // Red
  '#10b981', // Green
  '#f59e0b', // Amber
  '#8b5cf6', // Violet
  '#ec4899', // Pink
  '#14b8a6', // Teal
  '#f97316', // Orange
  '#06b6d4', // Cyan
  '#a855f7', // Purple
  '#84cc16', // Lime
  '#f43f5e', // Rose
  '#0ea5e9', // Sky
  '#eab308', // Yellow
  '#22c55e', // Green-500
  '#6366f1', // Indigo
  '#fb923c', // Orange-400
  '#a78bfa', // Violet-400
  '#4ade80', // Green-400
  '#facc15', // Yellow-400
]

const Route = ({ route, index, onRouteClick }) => {
  // Leaflet expects [lat, lng] but GeoJSON uses [lng, lat], so we need to swap
  const positions = useMemo(() => {
    if (!route.coordinates || route.coordinates.length === 0) {
      return []
    }
    // Convert GeoJSON coordinates [lng, lat] to Leaflet format [lat, lng]
    return route.coordinates.map(([lng, lat]) => [lat, lng])
  }, [route.coordinates])

  const color = COLORS[index % COLORS.length]

  const eventHandlers = useMemo(() => ({
    click: () => {
      // Navigate to route details when clicked
      if (onRouteClick) {
        onRouteClick(route)
      }
    },
    mouseover: (e) => {
      const target = e.target
      target.setStyle({
        weight: 8,
        opacity: 1,
        color: '#ff6b35' // Orange highlight
      })
      target.openTooltip()
    },
    mouseout: (e) => {
      const target = e.target
      target.setStyle({
        weight: 4,
        opacity: 0.75,
        color: color
      })
      target.closeTooltip()
    }
  }), [color, onRouteClick, route])

  // Don't render if no valid coordinates
  if (positions.length === 0) {
    return null
  }

  // Calculate distance in km from coordinates
  const calculateDistance = () => {
    if (!route.distance) {
      // Could calculate from coordinates if needed
      return 'N/A'
    }
    return `${route.distance.toFixed(1)} km`
  }

  return (
    <Polyline
      positions={positions}
      pathOptions={{
        color: color,
        weight: 4,
        opacity: 0.75,
        lineCap: 'round',
        lineJoin: 'round'
      }}
      eventHandlers={eventHandlers}
    >
      <Tooltip 
        permanent={false} 
        sticky 
        className="route-tooltip"
        direction="auto"
        opacity={0.95}
      >
        <div className="p-3 min-w-[220px] font-sans bg-white rounded-lg shadow-lg">
          {/* Route Name */}
          <div className="font-bold text-lg mb-2 text-gray-800">
            {route.name}
          </div>
          
          {/* Route Details */}
          <div className="space-y-2 text-sm">
            {/* Start Point */}
            {route.startPoint && (
              <div className="flex justify-between items-start">
                <span className="font-medium text-gray-600 mr-2">Start:</span>
                <span className="text-gray-800 text-right flex-1">{route.startPoint}</span>
              </div>
            )}
            
            {/* End Point */}
            {route.endPoint && (
              <div className="flex justify-between items-start">
                <span className="font-medium text-gray-600 mr-2">End:</span>
                <span className="text-gray-800 text-right flex-1">{route.endPoint}</span>
              </div>
            )}
            
            {/* Distance */}
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-600">Distance:</span>
              <span className="text-gray-800 font-bold">{calculateDistance()}</span>
            </div>
            
            {/* Difficulty Badge */}
            {route.difficulty && (
              <div className="flex justify-between items-center">
                <span className="font-medium text-gray-600">Difficulty:</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  route.difficulty === 'Easy' ? 'bg-green-100 text-green-800' :
                  route.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {route.difficulty}
                </span>
              </div>
            )}
            
            {/* Description */}
            {route.description && (
              <div className="pt-2 border-t border-gray-200">
                <p className="text-xs text-gray-600 italic">{route.description}</p>
              </div>
            )}

            {/* Creator Info */}
            {route.creator && (
              <div className="pt-2 border-t border-gray-200">
                <div className="text-xs text-gray-500">
                  <span>Created by: </span>
                  <span className="font-medium">{route.creator.name}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </Tooltip>
    </Polyline>
  )
}

export default Route
